import apiClient, { ApiResponse } from './client';

// ═══════════════════════════════════════════════════════════════════
// TYPES (mirror apps/api publish-channels/publish-channels.service.ts)
// ═══════════════════════════════════════════════════════════════════

export type PublishChannelType = 'MARKETPLACE' | 'SHOPIFY';

export interface PublishChannel {
  id: string;
  type: PublishChannelType;
  /** Stable key; an iframe URL is matched against this, never the name. */
  key: string;
  displayName: string;
  /** False once the partner reported the store as uninstalled. */
  isActive: boolean;
  /**
   * PULL: the partner app fetches this store's products itself, so the store
   * shows up in the overview but is never offered as a target. Absent means
   * PUSH, which is what an API from before this field sends.
   */
  deliveryMode?: 'PUSH' | 'PULL';
}

/**
 * Cloud sessions hand over through the cloud callback and never see a channel
 * selection. The server decides this, so the web app does not have to guess it
 * from the session.
 */
export interface ChannelResolution {
  mode: 'cloud' | 'channels';
  channels: PublishChannel[];
}

// ═══════════════════════════════════════════════════════════════════
// API FUNCTIONS
// ═══════════════════════════════════════════════════════════════════

export async function getPublishChannels(): Promise<ChannelResolution> {
  const response =
    await apiClient.get<ApiResponse<ChannelResolution>>('/publish-channels');
  return response.data.data;
}

/** What a publish call reports back. */
export interface DispatchResult {
  enqueued: number;
  enqueuedChannelIds: string[];
  skipped: Array<{
    designId: string;
    channelId?: string;
    reason: string;
  }>;
}

export async function publishToChannels(
  designId: string,
  channelIds: string[]
): Promise<DispatchResult> {
  const response = await apiClient.post<ApiResponse<DispatchResult>>(
    `/designs/${designId}/publish-channels`,
    { channelIds }
  );
  return response.data.data;
}

export async function sendListingsToChannels(
  designIds: string[],
  channelIds: string[]
): Promise<DispatchResult> {
  const response = await apiClient.post<ApiResponse<DispatchResult>>(
    '/publish-channels/bulk',
    { designIds, channelIds }
  );
  return response.data.data;
}
